import { ModalIOF } from '@old/ui/modal/modal';
import { configButtonStylesContained, configButtonStylesOutlined } from '@old/styles/generic-styles';
import i18n from '../../libs/language';
import { listSilobagService } from '@old/services/domain/base/silobag/base/silobag-list';
import { Button, Typography, CircularProgress } from '@mui/material';
import { useService } from '@old/hooks/use-service';
import { archiveSilobagService } from '@old/services/domain/base/silobag/base/archive-silobag';
import { useState } from 'react';
import { DYNAMIC_PATH } from '@old/data/core/paths';
import { useNavigate } from 'react-router';
type SummarySilobagList = Awaited<ReturnType<typeof listSilobagService>>['summary']['data'];
type ToastArchivable = {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info' | 'warning';
};
type ArchivableSbModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  silobags: SummarySilobagList;
  setToastArchivable: (data: ToastArchivable) => void;
};

export const ArchivableSbModal = ({
  open,
  setOpen,
  silobags,
  setToastArchivable,
}: ArchivableSbModalProps) => {
  const archivableSb = silobags.filter((sb) => sb.v_archivable_silobags.can_archive && sb.write_permission);
  const archibableSbIds = silobags
    .filter((sb) => sb.v_archivable_silobags.can_archive && sb.write_permission)
    .map((sb) => sb.id);
  const noArchivableSb = silobags.filter(
    (sb) => !sb.v_archivable_silobags.can_archive || !sb.write_permission
  );
  const [loading, setLoading] = useState(false);
  const { trigger: postArchiveSilobag } = useService(archiveSilobagService);
  const navigate = useNavigate();
  const handleArchiveSilobags = async () => {
    let successCount = 0;
    let errorCount = 0;

    for (const id of archibableSbIds) {
      try {
        await postArchiveSilobag({ query: id });
        setLoading(true);
        successCount++;
      } catch {
        errorCount++;
      }
    }
    setOpen(false);
    setLoading(false);

    let toastData: ToastArchivable;
    switch (true) {
      case successCount > 0 && errorCount === 0:
        toastData = {
          open: true,
          message: `Todas las silobolsas (${successCount}) fueron archivadas correctamente.`,
          severity: 'success',
        };
        navigate(DYNAMIC_PATH.ARCHIVED);
        break;

      case successCount > 0 && errorCount > 0:
        toastData = {
          open: true,
          message: `${successCount} silobolsas archivadas correctamente. ${errorCount} fallaron.`,
          severity: 'info',
        };
        navigate(DYNAMIC_PATH.ARCHIVED);
        break;

      default:
        toastData = {
          open: true,
          message: i18n.t('app.archived_silobags.no_archived_silobags'),
          severity: 'error',
        };
        break;
    }
    setToastArchivable(toastData);
  };

  return (
    <>
      <ModalIOF open={open} ModalIsOpen={setOpen}>
        <ModalIOF.Header>
          <ModalIOF.Title>{i18n.t('app.archived_silobags.archive')}</ModalIOF.Title>
        </ModalIOF.Header>
        <ModalIOF.Body>
          <div>
            {noArchivableSb.length > 0 && (
              <div>
                <Typography variant="h4" sx={{ fontSize: '17px', fontWeight: 500 }}>
                  {i18n.t('app.archived_silobags.modal_permission_question')}
                </Typography>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '5px',
                    color: 'red',
                    fontWeight: 500,
                  }}
                >
                  <p>{noArchivableSb.map((sb) => sb.id).join(', ')}</p>
                </div>
              </div>
            )}
            {archivableSb.length > 0 && (
              <div>
                <Typography variant="h4" sx={{ fontSize: '17px', fontWeight: 500 }}>
                  {i18n.t('app.archived_silobags.modal_validation')}
                </Typography>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '5px',
                    color: '#6baa00',
                    fontWeight: 500,
                  }}
                >
                  <p>{archivableSb.map((sb) => sb.id).join(', ')}</p>
                </div>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'end', marginTop: '20px' }}>
            <Button variant="outlined" sx={configButtonStylesOutlined} onClick={() => setOpen(false)}>
              {i18n.t('app.close')}
            </Button>
            <Button
              variant="contained"
              sx={configButtonStylesContained}
              onClick={handleArchiveSilobags}
              disabled={archivableSb.length === 0}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: '#fff' }} />
              ) : (
                i18n.t('app.archived_silobags.archive_button')
              )}
            </Button>
          </div>
        </ModalIOF.Body>
      </ModalIOF>
    </>
  );
};
